import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';

const API = API_BASE_URL;

/**
 * SearchOutreachTab — outreach data scoped to a specific search.
 *
 * Three sub-tabs, mirroring the /outreach page but filtered to this
 * search's activity:
 *   - Campaigns: campaigns with candidates enrolled from this pipeline
 *   - Tasks: pending outreach tasks tied to those enrollments
 *   - Analytics: per-campaign delta breakdown (this month vs. last)
 *
 * All three read from `/api/outreach/search/:pipelineId/...` — the
 * existing pattern this component already uses for `/summary` and
 * `/campaign/:id/sequences`. New endpoints added server-side:
 *   GET /api/outreach/search/:pipelineId/tasks
 *   GET /api/outreach/search/:pipelineId/analytics
 */
const SearchOutreachTab = ({ pipelineId, pipelineName }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('campaigns');

  const token = localStorage.getItem('authToken');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  // ── CAMPAIGNS TAB STATE ────────────────────────────────────────────
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedCampaign, setExpandedCampaign] = useState(null);
  const [sequences, setSequences] = useState([]);
  const [seqLoading, setSeqLoading] = useState(false);

  const loadSummary = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/outreach/search/${pipelineId}/summary`, { headers });
      if (res.ok) setSummary(await res.json());
    } catch (e) { console.error('[banyak-search-outreach] summary fetch failed', e); }
    finally { setLoading(false); }
  }, [pipelineId]);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  const loadSequences = async (campaignId) => {
    setSeqLoading(true);
    try {
      const res = await fetch(`${API}/api/outreach/search/${pipelineId}/campaign/${campaignId}/sequences`, { headers });
      if (res.ok) setSequences(await res.json());
    } catch (e) { console.error(e); }
    finally { setSeqLoading(false); }
  };

  const toggleCampaign = (campaignId) => {
    if (expandedCampaign === campaignId) {
      setExpandedCampaign(null);
      setSequences([]);
    } else {
      setExpandedCampaign(campaignId);
      loadSequences(campaignId);
    }
  };

  const updateSequence = async (seqId, status) => {
    try {
      await fetch(`${API}/api/outreach/sequences/${seqId}/status`, { method: 'PUT', headers, body: JSON.stringify({ status }) });
      if (expandedCampaign) loadSequences(expandedCampaign);
      loadSummary();
    } catch (e) { console.error(e); }
  };

  const removeSequence = async (seqId) => {
    if (!window.confirm('Remove this candidate from the campaign?')) return;
    try {
      await fetch(`${API}/api/outreach/sequences/${seqId}`, { method: 'DELETE', headers });
      if (expandedCampaign) loadSequences(expandedCampaign);
      loadSummary();
    } catch (e) { console.error(e); }
  };

  // ── TASKS TAB STATE ────────────────────────────────────────────────
  const [tasks, setTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [tasksError, setTasksError] = useState(null);

  const loadTasks = useCallback(async () => {
    setTasksLoading(true);
    setTasksError(null);
    try {
      const res = await fetch(`${API}/api/outreach/search/${pipelineId}/tasks`, { headers });
      if (!res.ok) {
        console.error('[banyak-search-outreach] tasks fetch failed', res.status);
        setTasksError(res.status === 404 ? 'Endpoint not deployed yet' : 'Failed to load tasks');
        return;
      }
      setTasks(await res.json());
    } catch (e) {
      console.error('[banyak-search-outreach] tasks fetch threw', e);
      setTasksError('Network error loading tasks');
    } finally {
      setTasksLoading(false);
    }
  }, [pipelineId]);

  const completeTask = async (taskId) => {
    try {
      await fetch(`${API}/api/outreach/tasks/${taskId}/complete`, { method: 'PUT', headers });
      loadTasks();
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    if (activeTab === 'tasks') loadTasks();
  }, [activeTab, loadTasks]);

  // ── ANALYTICS TAB STATE ────────────────────────────────────────────
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState(null);

  const loadAnalytics = useCallback(async () => {
    setAnalyticsLoading(true);
    setAnalyticsError(null);
    try {
      const res = await fetch(`${API}/api/outreach/search/${pipelineId}/analytics`, { headers });
      if (!res.ok) {
        console.error('[banyak-search-outreach] analytics fetch failed', res.status);
        setAnalyticsError(res.status === 404 ? 'Endpoint not deployed yet' : 'Failed to load analytics');
        return;
      }
      setAnalytics(await res.json());
    } catch (e) {
      console.error('[banyak-search-outreach] analytics fetch threw', e);
      setAnalyticsError('Network error loading analytics');
    } finally {
      setAnalyticsLoading(false);
    }
  }, [pipelineId]);

  useEffect(() => {
    if (activeTab === 'analytics') loadAnalytics();
  }, [activeTab, loadAnalytics]);

  // ── SHARED HELPERS ────────────────────────────────────────────────
  const seqStatusColors = {
    active: 'bg-green-100 text-green-700',
    paused: 'bg-yellow-100 text-yellow-700',
    completed: 'bg-gray-100 text-gray-600',
    replied: 'bg-blue-100 text-blue-700',
    bounced: 'bg-red-100 text-red-600',
    opted_out: 'bg-red-100 text-red-600',
    waiting_task: 'bg-purple-100 text-purple-700',
  };

  const deltaPct = (curr, prev) => {
    if (curr == null || prev == null) return null;
    if (prev === 0) return curr === 0 ? 0 : null;
    return Math.round(((curr - prev) / prev) * 100);
  };

  const DeltaBadge = ({ current, previous }) => {
    const pct = deltaPct(current, previous);
    if (pct === null) return <span className="text-[10px] text-gray-400">—</span>;
    if (pct === 0) return <span className="text-[10px] text-gray-500 font-medium">0%</span>;
    const up = pct > 0;
    return (
      <span className={`text-[10px] font-medium ${up ? 'text-emerald-600' : 'text-red-600'}`}>
        {up ? '+' : ''}{pct}%
      </span>
    );
  };

  const { campaigns = [], totals = {} } = summary || {};

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Tab strip — sits at the top of the SearchOutreachTab body,
          under the SearchDetailPage sub-tab strip. Matches the pill
          nav style used on /outreach and the top-level pageStrip. */}
      <div className="bg-white border-b px-4 h-10 flex items-center gap-1.5 flex-shrink-0">
        {[
          { key: 'campaigns', label: 'Campaigns' },
          { key: 'tasks',     label: 'Tasks' },
          { key: 'analytics', label: 'Analytics' },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            className={`flex-shrink-0 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
              activeTab === t.key
                ? 'bg-emerald-100 text-emerald-800'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ─── CAMPAIGNS TAB ─── */}
      {activeTab === 'campaigns' && (
        <div className="flex-1 overflow-auto">
          <div className="p-4 max-w-5xl mx-auto">
            {loading ? (
              <div className="flex items-center justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>
            ) : (
              <>
                {campaigns.length > 0 && (
                  <div className="grid grid-cols-6 gap-3 mb-6">
                    {[
                      { label: 'Campaigns', value: totals.campaigns || 0, color: 'text-gray-900' },
                      { label: 'Enrolled', value: totals.total_enrolled || 0, color: 'text-blue-600' },
                      { label: 'Active', value: totals.active || 0, color: 'text-green-600' },
                      { label: 'Replied', value: totals.replied || 0, color: 'text-emerald-600' },
                      { label: 'Emails Sent', value: totals.emails_sent || 0, color: 'text-indigo-600' },
                      { label: 'Response Rate', value: `${totals.response_rate || 0}%`, color: totals.response_rate > 20 ? 'text-green-600' : totals.response_rate > 10 ? 'text-yellow-600' : 'text-gray-600' },
                    ].map(stat => (
                      <div key={stat.label} className="bg-white rounded-lg border p-3 text-center">
                        <div className={`text-lg font-bold ${stat.color}`}>{stat.value}</div>
                        <div className="text-[10px] text-gray-400 uppercase tracking-wide mt-0.5">{stat.label}</div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-sm font-semibold text-gray-700">Campaigns for this Search</h2>
                  <div className="flex items-center gap-2">
                    <button onClick={() => navigate('/outreach')}
                      className="px-3 py-1.5 text-xs border rounded-lg text-gray-600 hover:bg-gray-50">
                      All Campaigns
                    </button>
                    <button onClick={() => navigate(`/outreach/new`)}
                      className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-1">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                      New Campaign
                    </button>
                  </div>
                </div>

                {campaigns.length === 0 ? (
                  <div className="bg-white rounded-lg border border-dashed p-10 text-center">
                    <svg className="w-10 h-10 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <p className="text-sm text-gray-500 font-medium">No outreach for this search yet</p>
                    <p className="text-xs text-gray-400 mt-1">Enroll candidates from the Search Pool into a campaign to start outreach</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {campaigns.map(c => {
                      const isExpanded = expandedCampaign === c.id;
                      const campaignStatusColors = {
                        active: 'bg-green-100 text-green-700',
                        draft: 'bg-gray-100 text-gray-600',
                        paused: 'bg-yellow-100 text-yellow-700',
                        archived: 'bg-red-100 text-red-600',
                      };

                      return (
                        <div key={c.id} className="bg-white rounded-lg border shadow-sm overflow-hidden">
                          <div className="px-4 py-3 flex items-center gap-3 cursor-pointer hover:bg-gray-50"
                            onClick={() => toggleCampaign(c.id)}>
                            <svg className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ${isExpanded ? 'rotate-90' : ''}`}
                              fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-semibold text-gray-900 truncate">{c.name}</span>
                                <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${campaignStatusColors[c.status] || campaignStatusColors.draft}`}>{c.status}</span>
                                <span className="text-[10px] text-gray-400">{c.step_count} steps</span>
                              </div>
                            </div>
                            <div className="flex items-center gap-3 text-[11px] tabular-nums flex-shrink-0">
                              <span className="text-gray-500">{c.total_enrolled} enrolled</span>
                              {c.active_count > 0 && <span className="text-green-600">{c.active_count} active</span>}
                              {c.replied_count > 0 && <span className="text-blue-600">{c.replied_count} replied</span>}
                              {c.emails_sent > 0 && <span className="text-indigo-500">{c.emails_sent} sent</span>}
                            </div>
                            <button onClick={(e) => { e.stopPropagation(); navigate(`/outreach/${c.id}`); }}
                              className="px-2 py-1 text-[10px] border rounded text-gray-500 hover:bg-gray-100 flex-shrink-0">
                              Edit
                            </button>
                          </div>

                          {isExpanded && (
                            <div className="border-t">
                              {seqLoading ? (
                                <div className="flex items-center justify-center py-6"><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div></div>
                              ) : sequences.length === 0 ? (
                                <div className="px-4 py-6 text-center text-xs text-gray-400">No candidates from this search enrolled in this campaign</div>
                              ) : (
                                <table className="w-full text-xs">
                                  <thead>
                                    <tr className="bg-gray-50 border-b">
                                      <th className="px-4 py-2 text-left font-medium text-gray-500">Candidate</th>
                                      <th className="px-3 py-2 text-left font-medium text-gray-500">Status</th>
                                      <th className="px-3 py-2 text-left font-medium text-gray-500">Step</th>
                                      <th className="px-3 py-2 text-left font-medium text-gray-500">Emails</th>
                                      <th className="px-3 py-2 text-left font-medium text-gray-500">Last Activity</th>
                                      <th className="px-3 py-2 text-right font-medium text-gray-500">Actions</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-100">
                                    {sequences.map(seq => (
                                      <tr key={seq.id} className="hover:bg-gray-50">
                                        <td className="px-4 py-2">
                                          <div className="flex items-center gap-2">
                                            {seq.candidate_photo ? (
                                              <img src={seq.candidate_photo} alt="" className="w-6 h-6 rounded-full object-cover" />
                                            ) : (
                                              <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-[10px] font-medium text-gray-500">
                                                {seq.candidate_name?.charAt(0)?.toUpperCase()}
                                              </div>
                                            )}
                                            <div>
                                              <div className="font-medium text-gray-900">{seq.candidate_name}</div>
                                              <div className="text-gray-400">{seq.candidate_company || seq.candidate_title || ''}</div>
                                            </div>
                                          </div>
                                        </td>
                                        <td className="px-3 py-2">
                                          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${seqStatusColors[seq.status] || 'bg-gray-100 text-gray-600'}`}>{seq.status}</span>
                                        </td>
                                        <td className="px-3 py-2 text-gray-600">{(seq.current_step_position || 0) + 1} / {seq.total_steps || '?'}</td>
                                        <td className="px-3 py-2 text-gray-500">{seq.emails_sent || 0}</td>
                                        <td className="px-3 py-2 text-gray-400">{seq.last_activity ? new Date(seq.last_activity).toLocaleDateString() : '—'}</td>
                                        <td className="px-3 py-2 text-right">
                                          <div className="flex items-center gap-1 justify-end">
                                            {seq.status === 'active' && (
                                              <button onClick={() => updateSequence(seq.id, 'paused')} className="px-2 py-0.5 text-[10px] border rounded hover:bg-yellow-50 text-yellow-600">Pause</button>
                                            )}
                                            {(seq.status === 'paused' || seq.status === 'waiting_task') && (
                                              <button onClick={() => updateSequence(seq.id, 'active')} className="px-2 py-0.5 text-[10px] border rounded hover:bg-green-50 text-green-600">Resume</button>
                                            )}
                                            <button onClick={() => removeSequence(seq.id)} className="px-2 py-0.5 text-[10px] border rounded hover:bg-red-50 text-red-500">Remove</button>
                                          </div>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* ─── TASKS TAB ─── */}
      {activeTab === 'tasks' && (
        <div className="flex-1 overflow-auto">
          <div className="p-4 max-w-5xl mx-auto">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-gray-700">Outreach tasks for this search</h2>
              <span className="text-[11px] text-gray-400">{tasks.length} pending</span>
            </div>
            {tasksLoading ? (
              <div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div></div>
            ) : tasksError ? (
              <div className="bg-white rounded-lg border border-dashed p-8 text-center">
                <p className="text-sm text-gray-500">{tasksError}</p>
              </div>
            ) : tasks.length === 0 ? (
              <div className="bg-white rounded-lg border border-dashed p-10 text-center">
                <svg className="w-10 h-10 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm text-gray-500 font-medium">No pending outreach tasks</p>
                <p className="text-xs text-gray-400 mt-1">Tasks appear here when a campaign step requires manual action.</p>
              </div>
            ) : (
              <div className="bg-white rounded-lg border divide-y">
                {tasks.map(task => (
                  <div key={task.id} className="px-4 py-3 flex items-center gap-3 hover:bg-gray-50">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-sm font-medium text-gray-900 truncate">{task.title || 'Untitled task'}</span>
                        {task.campaign_name && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 font-medium flex-shrink-0">
                            {task.campaign_name}
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-gray-500 flex items-center gap-3">
                        {task.candidate_name && (
                          <span className="flex items-center gap-1">
                            <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                            {task.candidate_name}
                          </span>
                        )}
                        {task.created_at && (
                          <span>{new Date(task.created_at).toLocaleDateString()}</span>
                        )}
                      </div>
                      {task.description && (
                        <div className="text-[11px] text-gray-500 mt-1 line-clamp-2">{task.description}</div>
                      )}
                    </div>
                    <button
                      onClick={() => completeTask(task.id)}
                      className="px-3 py-1.5 text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700 flex-shrink-0"
                    >
                      Complete
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ─── ANALYTICS TAB ─── */}
      {activeTab === 'analytics' && (
        <div className="flex-1 overflow-auto">
          <div className="p-4 max-w-5xl mx-auto">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-gray-700">Analytics for this search</h2>
              <span className="text-[10px] text-gray-500 uppercase tracking-wide font-semibold">Messages Sent</span>
            </div>

            {analyticsLoading ? (
              <div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div></div>
            ) : analyticsError ? (
              <div className="bg-white rounded-lg border border-dashed p-8 text-center">
                <p className="text-sm text-gray-500">{analyticsError}</p>
              </div>
            ) : !analytics?.campaigns?.length ? (
              <div className="bg-white rounded-lg border border-dashed p-10 text-center">
                <p className="text-sm text-gray-500 font-medium">No analytics yet</p>
                <p className="text-xs text-gray-400 mt-1">Enroll candidates from this search to start seeing campaign metrics.</p>
              </div>
            ) : (
              <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-100">
                      <tr>
                        <th className="text-left px-4 py-2 text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Campaign</th>
                        <th className="text-right px-3 py-2 text-[10px] font-semibold text-gray-500 uppercase tracking-wide">This Month</th>
                        <th className="text-right px-3 py-2 text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Δ</th>
                        <th className="text-right px-3 py-2 text-[10px] font-semibold text-gray-500 uppercase tracking-wide">All Time</th>
                        <th className="text-right px-3 py-2 text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Pending</th>
                        <th className="text-right px-3 py-2 text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
                          Open Rate <span className="ml-1 text-[9px] text-gray-400 font-normal normal-case tracking-normal">(pending)</span>
                        </th>
                        <th className="text-right px-3 py-2 text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
                          Reply Rate <span className="ml-1 text-[9px] text-gray-400 font-normal normal-case tracking-normal">(pending)</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {analytics.campaigns.map(c => (
                        <tr key={c.id}
                          onClick={() => navigate(`/outreach/${c.id}`)}
                          className="hover:bg-emerald-50/40 cursor-pointer transition-colors">
                          <td className="px-4 py-2.5">
                            <div className="flex items-center gap-2">
                              <span className={`w-1.5 h-1.5 rounded-full ${
                                c.status === 'active' ? 'bg-emerald-500' :
                                c.status === 'paused' ? 'bg-amber-500' :
                                c.status === 'draft'  ? 'bg-gray-400' :
                                                        'bg-gray-300'
                              }`} />
                              <span className="text-sm font-medium text-gray-900 truncate">{c.name}</span>
                            </div>
                          </td>
                          <td className="text-right px-3 py-2.5 tabular-nums text-gray-900 font-semibold">
                            {(c.messages_this_month || 0).toLocaleString()}
                          </td>
                          <td className="text-right px-3 py-2.5">
                            <DeltaBadge current={c.messages_this_month} previous={c.messages_last_month} />
                          </td>
                          <td className="text-right px-3 py-2.5 tabular-nums text-gray-600">
                            {(c.messages_all_time || 0).toLocaleString()}
                          </td>
                          <td className="text-right px-3 py-2.5 tabular-nums text-gray-500">
                            {(c.messages_pending || 0).toLocaleString()}
                          </td>
                          <td className="text-right px-3 py-2.5 text-gray-300 text-xs">—</td>
                          <td className="text-right px-3 py-2.5 text-gray-300 text-xs">—</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="px-4 py-2 border-t border-gray-100 bg-gray-50 text-[10px] text-gray-500">
                  Open + Reply rates activate once email tracking is integrated (SendGrid / Mailgun webhooks or a tracking pixel).
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchOutreachTab;
